import type {
  PlanActivityState,
  PlanQueue,
  PlanQueueLane,
} from "../../Types/AccountPlan";

/** Seconds → "40 s", "1 min", "1½ min", "2 min" (rounded to the nearest 30 s past a minute). */
export const roughDuration = (secs: number): string => {
  if (secs < 55) return `${Math.max(5, Math.round(secs / 5) * 5)} s`;
  const halves = Math.max(2, Math.round(secs / 30));
  const whole = Math.floor(halves / 2);
  return `${whole}${halves % 2 ? "½" : ""} min`;
};

/** [low, high] seconds → "about 40 s", "about 1–1½ min", "about 2 min". Never a countdown. */
export const roughRange = (range: [number, number] | null | undefined) => {
  if (!range) return "";
  const [lo, hi] = range;
  const a = roughDuration(lo);
  const b = roughDuration(hi);
  if (a === b) return `about ${a}`;
  // "40 s–1½ min" reads badly: show the high end in minutes only when both are.
  if (a.endsWith(" min") && b.endsWith(" min"))
    return `about ${a.replace(" min", "")}–${b}`;
  if (a.endsWith(" s") && b.endsWith(" s"))
    return `about ${a.replace(" s", "")}–${b}`;
  return `about ${a}–${b}`;
};

export const aheadText = (n: number) =>
  n === 0 ? "You're next" : `${n} plan${n === 1 ? "" : "s"} ahead of yours`;

/** Our failure, retried until the limit and now with an Izba admin (design D29, audit
 * Q2): the only words people see for it. No error, no button, no bell. */
export const STALLED_LINE = "Taking longer than usual · Izba has been told";

/** The one-line status for a lane, as shown in the upload screen and the chip. */
export const laneLine = (
  lane: PlanQueueLane | null | undefined,
  what: "read" | "brief"
): string => {
  if (!lane) return "";
  if (lane.stalled) return STALLED_LINE;
  const doing = what === "read" ? "Reading" : "Writing the briefing";
  if (lane.state === "running") {
    if (lane.slow) return "Taking longer than usual";
    return `${doing} · ${roughRange(lane.done_in)} left`;
  }
  const start =
    lane.start_in && lane.start_in[1] <= 5
      ? "starting now"
      : `starts in ${roughRange(lane.start_in)}`;
  return `In line · ${aheadText(lane.ahead).toLowerCase()} · ${start}`;
};

/** Is it worth suggesting "Close, notify me" as the main action? Over a minute to go. */
export const suggestLeaving = (queue: PlanQueue | null | undefined) => {
  const lane = queue?.read || queue?.brief;
  if (!lane) return false;
  return lane.slow || (lane.done_in?.[1] ?? 0) > 60;
};

/** Chip label + Tailwind classes for a version's state (ledger and list). */
export const stateChip = (
  state: PlanActivityState,
  queue?: PlanQueue | null
): { label: string; cls: string } => {
  const read = queue?.read;
  const brief = queue?.brief;
  if (
    (read?.stalled && (state === "in_line" || state === "reading")) ||
    (brief?.stalled && (state === "brief_in_line" || state === "briefing"))
  )
    return { label: STALLED_LINE, cls: "bg-gray-100 text-gray-700" };
  switch (state) {
    case "in_line":
      return {
        label: read
          ? `In line · #${read.ahead + 1} · ${roughRange(read.done_in)}`
          : "In line",
        cls: "bg-gray-100 text-gray-700",
      };
    case "reading":
      return {
        label: read?.slow
          ? "Reading · longer than usual"
          : read
            ? `Reading · ${roughRange(read.done_in)}`
            : "Reading",
        cls: "bg-sky-100 text-sky-800",
      };
    case "ready":
      return { label: "Ready to view", cls: "bg-emerald-100 text-emerald-800" };
    case "ready_gaps":
      return { label: "Ready · with gaps", cls: "bg-amber-100 text-amber-800" };
    case "read_failed":
      return { label: "Couldn't read", cls: "bg-rose-100 text-rose-700" };
    case "brief_in_line":
      return {
        label: brief
          ? `Briefing in line · #${brief.ahead + 1}`
          : "Briefing in line",
        cls: "bg-gray-100 text-gray-700",
      };
    case "briefing":
      return {
        label: brief
          ? `Writing briefing · ${roughRange(brief.done_in)}`
          : "Writing briefing",
        cls: "bg-sky-100 text-sky-800",
      };
    case "briefing_failed":
      return { label: "Briefing failed", cls: "bg-rose-100 text-rose-700" };
    case "draft":
      return { label: "Draft briefing", cls: "bg-purple-100 text-purple-800" };
    case "in_review":
      return { label: "In review", cls: "bg-purple-100 text-purple-800" };
    case "published":
      return { label: "Published", cls: "bg-emerald-100 text-emerald-800" };
    case "withdrawn":
      return { label: "Withdrawn", cls: "bg-gray-100 text-gray-500" };
    case "needs_fixing":
      return { label: "Needs review", cls: "bg-rose-100 text-rose-700" };
    case "needs_ok":
      return {
        label: "Waiting for review",
        cls: "bg-amber-100 text-amber-800",
      };
    // Confirm plan (Phase 2): read, waiting on the AE before anything is written.
    case "needs_confirm":
      return {
        label: "Waiting to confirm",
        cls: "bg-amber-100 text-amber-800",
      };
    case "discarded":
      return { label: "Discarded", cls: "bg-gray-100 text-gray-500" };
    // Phase 3: a stopped upload whose stored file was deleted after 7 (or 14) days.
    case "expired":
      return {
        label: "Deleted after 7 days",
        cls: "bg-gray-100 text-gray-500",
      };
    case "superseded":
      return {
        label: "Replaced by a newer version",
        cls: "bg-gray-100 text-gray-500",
      };
    default:
      return { label: "Deleted", cls: "bg-gray-100 text-gray-500" };
  }
};

/** Ledger log lines, in plain words. */
export const EVENT_LABELS: Record<string, string> = {
  uploaded: "Uploaded",
  read_started: "Reading started",
  ready: "Ready to view",
  ready_partial: "Ready, with gaps",
  read_failed: "Couldn't read the file",
  withdrawn: "Upload withdrawn",
  // D23: deleted when a newer upload became the plan.
  replaced: "Replaced by a newer upload · deleted",
  retried: "Tried again",
  briefing_requested: "Draft briefing requested",
  briefing_started: "Writing the briefing",
  briefing_ready: "Draft briefing ready",
  briefing_failed: "The briefing couldn't be written",
  published: "Published to the sponsor",
  deleted: "Version deleted",
  rejected: "Not used: a plan check failed",
  needs_review: "Flagged for a quick look",
  confirmed: "Confirmed by the AE",
  discarded: "Discarded: not the right file",
  restored: "Discard undone",
  // Phase 3: refused before reading, and what the AE did about it.
  upload_rejected: "Upload rejected",
  rejection_dismissed: "Dismissed a rejection",
  moved_away: "Moved an upload to another account",
  overridden: "Used anyway",
  verified: "Sections verified",
};
